// Company settings — the /settings page's General tab. One read serves both
// cards: the company's own profile at the top level, and its display formats
// nested under `formatSettings`. Each card saves through its own POST, and
// both endpoints take the FULL section object rather than a diff, so a save
// always sends every field of its card.
//
// Typed against the confirmed sample (2026-09-30). `website`, `address` and
// `country` come back null, and so does the whole `formatSettings` object for a
// company that has never saved its formats — everything else is always present,
// so only those get a fallback and the rest map bare.
//
// Delete-company has no endpoint yet — see DeleteCompanyCard.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import { CompanyEmployeeRange } from './companyApi';
import { defaultCountry } from '@/mocks/countries';
import type { AppLanguage } from '@/redux/language/languageSlice';

/** Phone pickers seed to this when nothing is stored, so the dial `<select>`
 *  always has a matching option. */
const DEFAULT_DIAL_CODE = defaultCountry.dial;

/** Wire vocabulary for `startOfWeek`: lowercase English day names. */
export type WeekDay =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

interface ApiFormatSettings {
  mainLanguage: AppLanguage;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
  startOfWeek: WeekDay;
}

/** Additional settings → Custom branding. `media` is a mediaId, resolved to a
 *  URL through media/company-profile-media/:id at render time. */
interface ApiBrandDetails {
  media: string | null;
  brandColorScheme: string | null;
}

/** Additional settings → Mobile app welcome screen. Both link fields are ''
 *  when the owner has not enabled that legal link — there is no separate
 *  boolean on the wire, emptiness IS the off state. */
interface ApiWelcomeScreen {
  media: string | null;
  title: string | null;
  description: string | null;
  termsConditionsLink: string | null;
  privacyPolicyLink: string | null;
}

/** Additional settings → Contact administrator. The two numbers are split into
 *  dial code + national digits, matching PhoneInput and every other phone field
 *  in the app; the dial code carries its leading '+'. */
interface ApiSupportContact {
  name: string | null;
  email: string | null;
  mobileNumberCountryCode: string | null;
  mobileNumber: string | null;
  whatsAppNumberCountryCode: string | null;
  whatsAppNumber: string | null;
  supportDays: string | null;
  supportStartTime: string | null;
  supportEndTime: string | null;
}

interface ApiCompanyDetails {
  _id: string;
  companyName: string;
  email: string;
  companyCode: string;
  website: string | null;
  businessIndustry: string;
  address: string | null;
  country: string | null;
  employeeRange: CompanyEmployeeRange;
  /** null until the company saves its formats for the first time. */
  formatSettings: ApiFormatSettings | null;
  /** null until the company saves its branding for the first time. */
  brandDetails: ApiBrandDetails | null;
  /** null until the company saves a welcome screen for the first time. */
  welcomeScreen: ApiWelcomeScreen | null;
  /** null until the company saves a support contact for the first time. */
  supportContact: ApiSupportContact | null;
}

/** The Formats card's data. `dateFormat`/`timeFormat`/`timeZone` are free
 *  strings on the wire; the tokens we store are fixed in companySettingsOptions. */
export interface FormatSettings {
  mainLanguage: AppLanguage;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
  startOfWeek: WeekDay;
}

export interface BrandDetails {
  /** mediaId, or '' when no logo is set. */
  media: string;
  /** Hex including the leading '#'. */
  brandColorScheme: string;
}

export interface WelcomeScreen {
  /** mediaId, or '' when no image is set. */
  media: string;
  title: string;
  description: string;
  /** '' when the link is switched off. */
  termsConditionsLink: string;
  /** '' when the link is switched off. */
  privacyPolicyLink: string;
}

export interface SupportContact {
  name: string;
  email: string;
  mobileNumberCountryCode: string;
  mobileNumber: string;
  whatsAppNumberCountryCode: string;
  whatsAppNumber: string;
  supportDays: string;
  supportStartTime: string;
  supportEndTime: string;
}

export interface CompanyDetails {
  id: string;
  name: string;
  email: string;
  /** Server-issued join code. Read-only — absent from the save payload. */
  code: string;
  website: string;
  industry: string;
  address: string;
  country: string;
  employeeRange: CompanyEmployeeRange;
  formats: FormatSettings;
  brand: BrandDetails;
  welcome: WelcomeScreen;
  support: SupportContact;
}

/** POST companies/company-details — the whole Company details card. */
export type UpdateCompanyDetailsInput = Omit<
  CompanyDetails,
  'id' | 'code' | 'formats' | 'brand' | 'welcome' | 'support'
>;

/** POST companies/company-settings-format — the whole Formats card. */
export type UpdateCompanyFormatsInput = FormatSettings;

/** Trims, and turns an untouched field into `null` — the whole Additional
 *  settings tab writes null rather than '' for "not entered". The UI models
 *  stay all-strings; only the wire is nullable. */
export const nullIfEmpty = (value: string): string | null => value.trim() || null;

/** Every writable field on a section is nullable on the wire. */
type Nullable<T> = { [K in keyof T]: T[K] | null };

/** POST companies/brand-details — the whole Custom branding card. */
export type UpdateBrandDetailsInput = Nullable<BrandDetails>;

/** POST companies/welcome-screen — the whole welcome-screen card. */
export type UpdateWelcomeScreenInput = Nullable<WelcomeScreen>;

/** POST companies/support-contact — the whole Contact administrator card. */
export type UpdateSupportContactInput = Nullable<SupportContact>;

/** Shown when `formatSettings` is null — i.e. the company has never saved its
 *  formats. These are display defaults only: they are not written anywhere
 *  until the owner actually saves the Formats card. Values must stay inside the
 *  vocabularies in companySettingsOptions, or the selects render blank. */
export const DEFAULT_FORMAT_SETTINGS: FormatSettings = {
  mainLanguage: 'en',
  dateFormat: 'dd/mm/yyyy',
  timeFormat: '24 hrs',
  timeZone: 'Asia/Riyadh',
  startOfWeek: 'sunday',
};

const toFormatSettings = (res: ApiFormatSettings | null): FormatSettings =>
  res
    ? {
        mainLanguage: res.mainLanguage,
        dateFormat: res.dateFormat,
        timeFormat: res.timeFormat,
        timeZone: res.timeZone,
        startOfWeek: res.startOfWeek,
      }
    : { ...DEFAULT_FORMAT_SETTINGS };

/** Shown when `brandDetails` is null. The colour seeds to the Midani brand
 *  green so the swatch row has a selection rather than an empty state; it is
 *  not written anywhere until the owner saves. */
export const DEFAULT_BRAND_DETAILS: BrandDetails = {
  media: '',
  brandColorScheme: '#0E8F6E',
};

const toBrandDetails = (res: ApiBrandDetails | null): BrandDetails =>
  res
    ? {
        media: res.media ?? '',
        brandColorScheme: res.brandColorScheme ?? DEFAULT_BRAND_DETAILS.brandColorScheme,
      }
    : { ...DEFAULT_BRAND_DETAILS };

/** Shown when `welcomeScreen` is null. Everything empty: a company that has
 *  never configured one has no title to invent, and both legal links default
 *  to off. */
export const DEFAULT_WELCOME_SCREEN: WelcomeScreen = {
  media: '',
  title: '',
  description: '',
  termsConditionsLink: '',
  privacyPolicyLink: '',
};

const toWelcomeScreen = (res: ApiWelcomeScreen | null): WelcomeScreen =>
  res
    ? {
        media: res.media ?? '',
        title: res.title ?? '',
        description: res.description ?? '',
        termsConditionsLink: res.termsConditionsLink ?? '',
        privacyPolicyLink: res.privacyPolicyLink ?? '',
      }
    : { ...DEFAULT_WELCOME_SCREEN };

/** Shown when `supportContact` is null. The dial codes seed to the default
 *  country so a fresh card's phone pickers are never blank; everything else is
 *  empty, and none of it is written until the owner saves. */
export const DEFAULT_SUPPORT_CONTACT: SupportContact = {
  name: '',
  email: '',
  mobileNumberCountryCode: DEFAULT_DIAL_CODE,
  mobileNumber: '',
  whatsAppNumberCountryCode: DEFAULT_DIAL_CODE,
  whatsAppNumber: '',
  supportDays: '',
  supportStartTime: '',
  supportEndTime: '',
};

const toSupportContact = (res: ApiSupportContact | null): SupportContact =>
  res
    ? {
        name: res.name ?? '',
        email: res.email ?? '',
        // A stored contact predating the dial-code split, or one saved empty,
        // would leave the picker with no matching option — seed the default.
        mobileNumberCountryCode: res.mobileNumberCountryCode || DEFAULT_DIAL_CODE,
        mobileNumber: res.mobileNumber ?? '',
        whatsAppNumberCountryCode: res.whatsAppNumberCountryCode || DEFAULT_DIAL_CODE,
        whatsAppNumber: res.whatsAppNumber ?? '',
        supportDays: res.supportDays ?? '',
        supportStartTime: res.supportStartTime ?? '',
        supportEndTime: res.supportEndTime ?? '',
      }
    : { ...DEFAULT_SUPPORT_CONTACT };

const toCompanyDetails = (res: ApiCompanyDetails): CompanyDetails => ({
  id: res._id,
  name: res.companyName,
  email: res.email,
  code: res.companyCode,
  website: res.website ?? '',
  industry: res.businessIndustry,
  address: res.address ?? '',
  country: res.country ?? '',
  employeeRange: res.employeeRange,
  formats: toFormatSettings(res.formatSettings),
  brand: toBrandDetails(res.brandDetails),
  welcome: toWelcomeScreen(res.welcomeScreen),
  support: toSupportContact(res.supportContact),
});

const CURRENT = { type: 'CompanySettings' as const, id: 'CURRENT' };

export const companySettingsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCompanySettings: build.query<CompanyDetails, void>({
      query: () => ({ ...ROUTES.companies.companyDetails }),
      transformResponse: toCompanyDetails,
      providesTags: [CURRENT],
    }),
    // Also refreshes 'Company' (the switch-company list shows companyName) and
    // 'Profile' (/my-profile's membership card nests the same name).
    updateCompanyDetails: build.mutation<unknown, UpdateCompanyDetailsInput>({
      query: (body) => ({
        ...ROUTES.companies.updateCompanyDetails,
        data: {
          companyName: body.name,
          email: body.email,
          website: body.website,
          businessIndustry: body.industry,
          address: body.address,
          country: body.country,
          employeeRange: body.employeeRange,
        },
      }),
      invalidatesTags: [CURRENT, 'Company', 'Profile'],
    }),
    updateCompanyFormats: build.mutation<unknown, UpdateCompanyFormatsInput>({
      query: (body) => ({ ...ROUTES.companies.updateCompanyFormats, data: body }),
      invalidatesTags: [CURRENT],
    }),
    // Full-object save: a colour-only change still sends the current media id,
    // or the server would read it as a clear.
    updateBrandDetails: build.mutation<unknown, UpdateBrandDetailsInput>({
      query: (body) => ({ ...ROUTES.companies.updateBrandDetails, data: body }),
      invalidatesTags: [CURRENT],
    }),
    updateWelcomeScreen: build.mutation<unknown, UpdateWelcomeScreenInput>({
      query: (body) => ({ ...ROUTES.companies.updateWelcomeScreen, data: body }),
      invalidatesTags: [CURRENT],
    }),
    updateSupportContact: build.mutation<unknown, UpdateSupportContactInput>({
      query: (body) => ({ ...ROUTES.companies.updateSupportContact, data: body }),
      invalidatesTags: [CURRENT],
    }),
  }),
});

export const {
  useGetCompanySettingsQuery,
  useUpdateCompanyDetailsMutation,
  useUpdateCompanyFormatsMutation,
  useUpdateBrandDetailsMutation,
  useUpdateWelcomeScreenMutation,
  useUpdateSupportContactMutation,
} = companySettingsApi;
